import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import FileNameWithIcon from "../../../../components/fileNameWithIcon";
import { certificationType, sampleOfWorkType } from "utils/constantValues";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import useUploadFiles from "pages/signup/hooks/useUploadFiles";
import UploadFile from "components/uploadFile";
import { ReactComponent as AttachIcon } from "images/auth/attach.svg";
import { ReactComponent as EnterIcon } from "images/documents/EnterIcon.svg";
import useProfileHelper from "pages/profile/useProfileHelper";
import useProfileCertifications from "pages/profile/common/profileCertification/useProfileCertifications";
import useRelevantProfile from "pages/profile/vendorProfile/relevantProfiles/useRelevantProfile";
import Input from "components/input";
import { ReactComponent as EditIcon } from "images/profile/edit-icon.svg";

function CertificatesInformation({ onSave = () => {} }) {
  const [t] = useTranslation();
  const user = useSelector((state) => state.user || {});
  const isAgency = user?.isAgency ?? false;
  const userDetails = useSelector((state) => state.userDetails || {});
  const [relevantProfilesTitle, setRelevantProfilesTitle] = useState("");
  const certificationsFiles = userDetails?.userFiles
    ?.filter((file) => file.type === certificationType)
    .map((file) => ({ ...file, fileId: file.id }));
  const sampleOfWork = userDetails?.userFiles
    ?.filter((file) => file.type === sampleOfWorkType)
    .map((file) => ({ ...file, fileId: file.id }));
  const profileLinks = Array.isArray(userDetails?.extraInformation?.profile)
    ? userDetails.extraInformation.profile
    : [];
  const defaultsValues = {
    newCreated: [],
    deleted: [],
    vendorAdditionalInfo: {
      profile: profileLinks ?? [],
    },
  };

  const { register, handleSubmit, setValue, watch } = useForm({
    mode: "onChange",
    defaultValues: defaultsValues,
  });
  const {
    certificationsRef,
    uploadCertifications,
    isUploading,
    sampleOfWorkRef,
    uploadSampleOfFiles,
    removeSampleOfFile,
    removeCertificationFile,
  } = useUploadFiles({
    defaultCertificationsFiles: certificationsFiles,
    defaultSampleOfWorkFiles: sampleOfWork,
  });

  const { downloadFileInANewTab } = useProfileHelper();
  const { onEditCertificationsFiles } = useProfileCertifications({});

  const { onEditRelevantProfile } = useRelevantProfile({ userDetails });

  const addRelevantProfile = (value) => {
    const profileLink = value?.trim();

    if (!profileLink) return;

    onEditRelevantProfile({
      vendorAdditionalInfo: {
        profile: [...profileLinks, profileLink],
      },
    });
    setRelevantProfilesTitle("");
  };

  return (
    <form
      className="flex flex-col self-stretch rounded-[16px] "
      onSubmit={handleSubmit(onSave)}
    >
      <div
        id="certificates-information"
        className={`flex p-[24px]  gap-[24px] self-stretch`}
      >
        <div className="flex items-start gap-6 self-stretch">
          <div className="flex flex-col items-start w-[330px] gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[#1D1B22] text-center font-semibold text-[24px] leading-[36px] font-['General_Sans']">
                {t("certificates")}
              </span>
            </div>

            <div className="grid  gap-[12px] w-full">
              <UploadFile
                label="Document"
                name={"certifications"}
                register={register}
                placeHolderLabel={t("uploadCertification")}
                placeHolderContainerClassName="flex p-[14px] justify-between items-center gap-[8px] self-stretch rounded-[6px] h-[54px] w-full border-[1px] border-dashed border-dark-gray cursor-pointer"
                icon={<AttachIcon />}
                isUploading={isUploading.certifications}
                isMultiFile={true}
                getFile={(e, isDrag) => {
                  const callback = (type, files) => {
                    setValue(type, files);
                    setValue("newCreated", files);
                    onEditCertificationsFiles({ newCreated: files });
                  };

                  uploadCertifications(e, isDrag, callback, true);
                }}
                labelClassName="text-primary font-semibold text-[16px]"
                ref={certificationsRef}
                registerArgs={{
                  required: t("errorField", { field: t("certifications") }),
                }}
                isRequired={true}
              />
            </div>

            <div className="flex flex-wrap">
              {certificationsFiles.map((file, index) => (
                <>
                  <FileNameWithIcon
                    fileName={file.name}
                    showHover={true}
                    onDeleteHandler={() => {
                      const prevDeletedFiles = watch("deleted") || [];

                      const deletedFile = certificationsFiles.filter(
                        (file) => file.name
                      );
                      setValue("deleted", [
                        ...prevDeletedFiles,
                        ...deletedFile,
                      ]);

                      onEditCertificationsFiles({
                        deleted: [
                          {
                            fileId: file.fileId,
                          },
                        ],
                      });
                      removeCertificationFile(file.name);
                    }}
                    onDownloadHandler={() => downloadFileInANewTab(file.fileId)}
                    withoutBorder={certificationsFiles?.length === index + 1}
                  />
                </>
              ))}
            </div>
          </div>
        </div>
        {!isAgency && (
          <div className="flex items-start gap-6 self-stretch">
            <div className="flex flex-col items-start w-[330px] gap-3">
              <div className="flex items-center gap-2">
                <span className="text-[#1D1B22] text-center font-semibold text-[24px] leading-[36px] font-['General_Sans']">
                  {t("samplesOfWork")}
                </span>
              </div>
              <div className="grid  gap-[12px]  w-full">
                <UploadFile
                  name={"sampleOfWorks"}
                  register={register}
                  label="Document"
                  isMultiFile={true}
                  placeHolderLabel={t("attachYourFiles")}
                  placeHolderContainerClassName="flex p-[14px] justify-between items-center gap-[8px] self-stretch rounded-[6px] h-[54px] w-full border-[1px] border-dashed border-dark-gray cursor-pointer"
                  icon={<AttachIcon />}
                  isUploading={isUploading.sampleOfWork}
                  getFile={(e, isDrag) => {
                    const callback = (type, files) => {
                      setValue(type, files);
                      setValue("newCreated", files);
                      onEditRelevantProfile({ newCreated: files });
                    };
                    uploadSampleOfFiles(e, isDrag, callback, true);
                  }}
                  labelClassName="text-primary font-semibold text-[16px]"
                  ref={sampleOfWorkRef}
                  registerArgs={{
                    required: t("errorField", { field: t("sampleOfWorks") }),
                  }}
                  isRequired={true}
                />

                <div className="flex flex-wrap">
                  {sampleOfWork.map((file, index) => (
                    <>
                      <FileNameWithIcon
                        fileName={file.name}
                        showHover={true}
                        onDeleteHandler={() => {
                          const prevDeletedFiles = watch("deleted") || [];
                          const deletedFile = sampleOfWork.filter(
                            (file) => file.name
                          );
                          setValue("deleted", [
                            ...prevDeletedFiles,
                            ...deletedFile,
                          ]);

                          onEditRelevantProfile({
                            deleted: [{ fileId: file.fileId }],
                          });
                          removeSampleOfFile(file.name);
                        }}
                        onDownloadHandler={() =>
                          downloadFileInANewTab(file.fileId)
                        }
                        withoutBorder={sampleOfWork?.length === index + 1}
                      />
                    </>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {!isAgency && (
          <div className="flex items-start gap-6 self-stretch">
            <div className="flex flex-col items-start w-[330px] gap-3">
              <div className="flex items-center gap-2">
                <span className="text-[#1D1B22] text-center font-semibold text-[24px] leading-[36px] font-['General_Sans']">
                  {t("ProfileLinkTitle")}
                </span>
              </div>

              <div className="flex flex-col w-full gap-[10px]">
                <Fragment key={`${profileLinks?.length}`}>
                  <div className="flex gap-[10px] items-center w-full">
                    <Input
                      name={`vendorAdditionalInfo.profile.${0}`}
                      label={t("link")}
                      icon={
                        <button
                          type="button"
                          className="flex h-6 w-6 items-center justify-center"
                          aria-label={t("addAnotherProfile")}
                          onClick={() =>
                            addRelevantProfile(relevantProfilesTitle)
                          }
                        >
                          <EnterIcon />
                        </button>
                      }
                      value={relevantProfilesTitle}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addRelevantProfile(e.currentTarget.value);
                        }
                      }}
                      onChange={(e) => {
                        setRelevantProfilesTitle(e.target.value);
                      }}
                      placeholder={t("placeHolderType", {
                        title: t("ProfileLinkTitle").toLowerCase(),
                      })}
                      containerClassName="flex flex-col gap-[10px] w-full "
                      labelClassName="text-primary font-semibold text-[16px] "
                      inputClassName="flex h-[55px] w-full sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid border-default-color bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] pl-[14px] pr-12"
                    />
                  </div>
                </Fragment>

                {profileLinks.map((file, index) => {
                  return (
                    <div
                      key={`${file}-${index}`}
                      className="flex flex-wrap"
                    >
                      <FileNameWithIcon
                        fileName={file}
                        showHover={true}
                        withOutIcon={false}
                        clickableLink={true}
                        onDeleteHandler={() => {
                          const prevDeletedFiles = watch("deleted") || [];

                          const updatedDeletedFiles = Array.from(
                            new Set([...prevDeletedFiles, file])
                          );

                          const updatedProfileLinks = profileLinks.filter(
                            (f) => f !== file
                          );

                          setValue("deleted", updatedDeletedFiles);
                          setValue(
                            "vendorAdditionalInfo.profile",
                            updatedProfileLinks
                          );

                          onEditRelevantProfile({
                            vendorAdditionalInfo: {
                              profile: updatedProfileLinks,
                            },
                          });

                          removeSampleOfFile(file);
                        }}
                        withDownload={false}
                        withoutBorder={profileLinks?.length === index + 1}
                        CustomButton={
                          <span
                            className="flex w-6 h-6 justify-center items-center shrink-0"
                            onClick={() => {
                              setRelevantProfilesTitle(file);
                              const prevDeletedFiles = watch("deleted") || [];

                              const updatedDeletedFiles = Array.from(
                                new Set([...prevDeletedFiles, file])
                              );

                              const updatedProfileLinks = profileLinks.filter(
                                (f) => f !== file
                              );

                              setValue("deleted", updatedDeletedFiles);
                              setValue(
                                "vendorAdditionalInfo.profile",
                                updatedProfileLinks
                              );

                              onEditRelevantProfile({
                                vendorAdditionalInfo: {
                                  profile: updatedProfileLinks,
                                },
                              });

                              removeSampleOfFile(file);
                            }}
                          >
                            <EditIcon />
                          </span>
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </form>
  );
}

CertificatesInformation.propTypes = {
  onEditClick: PropTypes.func,
  isShowEditIcon: PropTypes.bool,
};

export default CertificatesInformation;
